import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Radio, Heart, AlertCircle, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

export default function AudioPlayer() {
  const {
    currentTrack, isPlaying, togglePlay, setIsPlaying, closeTrack,
    progress, setProgress,
    toggleSave, isSaved,
  } = useApp();

  const audioRef = useRef(null);
  const suppressEventsRef = useRef(false); // ignore DOM events that our own effect just caused
  const [hasError, setHasError] = useState(false);
  const [duration, setDuration] = useState(null);

  // Load new source whenever the track changes
  useEffect(() => {
    setHasError(false);
    setDuration(null);
    setProgress(0);
    const el = audioRef.current;
    if (!el || !currentTrack) return;
    const src = currentTrack.audio_url || currentTrack.stream_url || "";
    if (!src) { setHasError(true); return; }
    if (el.src !== src) {
      el.src = src;
      try { el.load(); } catch { /* ignore */ }
    }
  }, [currentTrack, setProgress]);

  // Drive real playback from React state
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !currentTrack) return;
    suppressEventsRef.current = true;
    if (isPlaying) {
      const p = el.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          setHasError(false);
        }).catch((err) => {
          // AbortError happens when pause() interrupts a pending play() — that's expected, not a failure.
          if (err && err.name === "AbortError") return;
          // eslint-disable-next-line no-console
          console.warn("Audio play failed:", err);
          setHasError(true);
          toast.error("The stream could not start. Please try again.");
          setIsPlaying(false);
        }).finally(() => {
          // Release the suppression flag on the next tick so real subsequent events flow through
          setTimeout(() => { suppressEventsRef.current = false; }, 0);
        });
      } else {
        setTimeout(() => { suppressEventsRef.current = false; }, 0);
      }
    } else {
      try { el.pause(); } catch { /* ignore */ }
      setTimeout(() => { suppressEventsRef.current = false; }, 0);
    }
  }, [isPlaying, currentTrack, setIsPlaying]);

  if (!currentTrack) return null;

  const isRadio = currentTrack.kind === "radio";
  const savedNow = isSaved(currentTrack.id);
  const isLive = isRadio || duration === null || duration === Infinity || !isFinite(duration);

  const fmt = (sec) => {
    if (!isFinite(sec) || sec == null) return "--:--";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const onLoaded = () => { setDuration(audioRef.current?.duration); setHasError(false); };
  const onTimeUpdate = () => {
    const el = audioRef.current;
    if (!el || !el.duration || !isFinite(el.duration)) return;
    setProgress(Math.min(100, (el.currentTime / el.duration) * 100));
  };
  const onEnded = () => { setProgress(100); setIsPlaying(false); };
  const onErrorEvt = () => {
    if (suppressEventsRef.current) return;
    setHasError(true);
    setIsPlaying(false);
    toast.error("The audio stream could not be played.");
  };
  // Sync UI state with real element state, but ignore self-caused events
  const onPlayEvt  = () => { if (suppressEventsRef.current) return; setIsPlaying(true);  setHasError(false); };
  const onPauseEvt = () => { if (suppressEventsRef.current) return; setIsPlaying(false); };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || isLive || !el.duration || !isFinite(el.duration)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = pct * el.duration;
    setProgress(pct * 100);
  };

  return (
    <div
      className="fixed left-0 right-0 z-40 border-t backdrop-blur-md"
      style={{
        bottom: "56px",
        background: "rgba(48, 43, 41, 0.96)",
        borderColor: "rgba(196, 154, 58, 0.4)",
        color: "var(--bg-cream)",
      }}
      data-testid="persistent-audio-player"
    >
      {/* Real HTML5 audio element — no crossOrigin (Icecast doesn't need it and it can silently block media). preload='auto' so the browser actually opens the connection. */}
      <audio
        ref={audioRef}
        data-testid="audio-element"
        preload="auto"
        onLoadedMetadata={onLoaded}
        onDurationChange={onLoaded}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        onError={onErrorEvt}
        onPlay={onPlayEvt}
        onPause={onPauseEvt}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4">
        {currentTrack.art && (
          <img src={currentTrack.art} alt={currentTrack.title}
            className="w-11 h-11 sm:w-12 sm:h-12 rounded-md object-cover flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isRadio && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest rounded-full px-2 py-0.5"
                style={{ background: "var(--gold)", color: "var(--burgundy)" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--burgundy)] pulse-gold" /> LIVE
              </span>
            )}
            <div className="font-serif text-sm sm:text-base truncate" data-testid="audio-title">{currentTrack.title}</div>
            {hasError && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest"
                style={{ color: "var(--gold-soft)" }} data-testid="audio-error-indicator">
                <AlertCircle className="w-3 h-3" /> unavailable
              </span>
            )}
          </div>
          <div className="text-[11px] sm:text-xs opacity-80 truncate">{currentTrack.host}</div>
          {!isLive && (
            <div className="mt-1.5 hidden sm:flex items-center gap-2 text-[10px] font-mono opacity-80">
              <span>{fmt((progress / 100) * (duration || 0))}</span>
              <div className="flex-1 h-1 rounded-full cursor-pointer" onClick={seek} style={{ background: "rgba(247,240,227,0.2)" }}>
                <div className="h-full rounded-full" style={{ width: `${progress}%`, background: "var(--gold)" }} />
              </div>
              <span>{fmt(duration)}</span>
            </div>
          )}
          {isLive && !hasError && (
            <div className="mt-1.5 hidden sm:flex items-center gap-2 text-[10px] font-mono opacity-75">
              <div className="waveform text-[color:var(--gold)]"><span/><span/><span/><span/><span/></div>
              <span>Streaming live</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {!isRadio && (
            <button data-testid="audio-prev-btn" className="p-2 rounded-full hover:bg-white/10 hidden sm:inline-flex" aria-label="Previous" type="button">
              <SkipBack className="w-4 h-4" />
            </button>
          )}
          <button data-testid="audio-toggle-btn" onClick={togglePlay}
            className="p-2.5 rounded-full" style={{ background: "var(--gold)", color: "var(--burgundy)" }}
            aria-label={isPlaying ? "Pause" : "Play"} type="button">
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          {!isRadio && (
            <button data-testid="audio-next-btn" className="p-2 rounded-full hover:bg-white/10 hidden sm:inline-flex" aria-label="Next" type="button">
              <SkipForward className="w-4 h-4" />
            </button>
          )}
          <button data-testid="audio-save-btn"
            onClick={() => { toggleSave(currentTrack, "teaching"); toast(savedNow ? "Removed from My Temple" : "Saved to My Temple"); }}
            className="p-2 rounded-full hover:bg-white/10" aria-label="Save" type="button">
            <Heart className={`w-4 h-4 ${savedNow ? "fill-current" : ""}`} style={{ color: savedNow ? "var(--gold)" : "inherit" }} />
          </button>
          {isRadio && <Radio className="w-4 h-4 hidden sm:inline-block opacity-70" />}
          <button
            data-testid="audio-close-btn"
            onClick={() => {
              try { audioRef.current?.pause(); audioRef.current && (audioRef.current.src = ""); } catch { /* ignore */ }
              closeTrack();
            }}
            className="p-2 rounded-full hover:bg-white/10"
            aria-label="Close player"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
